import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { HelmetProvider } from 'react-helmet-async'

import App from './App.tsx'
import { AuthProvider } from './contexts/AuthContext'
import { ToastProvider } from './components/shared/ToastProvider'
import { loadAllowedDomains } from './utils/emailValidation'
import './index.css'

// Warm the email-domain allowlist from the backend so invitation / user-form
// validation reflects Parameter Store rather than hardcoded fallbacks.
loadAllowedDomains()

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false
        }
        return failureCount < 3
      },
    },
  },
})

// Create Material-UI theme with Poolwerx branding
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#007f98', // Poolwerx primary teal blue
      dark: '#054467', // Poolwerx deep blue
      light: '#00a3bb', // Poolwerx accent teal
    },
    secondary: {
      main: '#00a3bb', // Poolwerx secondary teal
      light: '#f1fafc', // Poolwerx light blue background
    },
    background: {
      default: '#f7f7f7', // Poolwerx light gray background
      paper: '#ffffff',
    },
    success: {
      main: '#2e7d32',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#ed6c02',
    },
  },
  typography: {
    fontFamily: [
      'Montserrat', // Poolwerx brand font
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      color: '#054467', // Deep blue for main headings
    },
    h2: {
      fontWeight: 700,
      color: '#054467',
    },
    h3: {
      fontWeight: 600,
      color: '#007f98',
    },
    h4: {
      fontWeight: 600,
      color: '#007f98',
    },
    h5: {
      fontWeight: 600,
      color: '#007f98',
    },
    h6: {
      fontWeight: 600,
      color: '#007f98',
    },
    button: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    // Paper-level only - radius + shadow shared by every dialog. Header /
    // body / footer spacing deliberately lives inside BaseDialog (NOT here)
    // so the ~20 dialogs not yet migrated keep their own layout.
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 8,
          boxShadow: theme.shadows[8],
        }),
      },
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ToastProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </ToastProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>,
)